import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { cantineApi, studentsApi, qrApi } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ForkKnife, Warning, MagnifyingGlass, SignOut, QrCode, CalendarX } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import QrScanner from '../../components/mobile/QrScanner';

/**
 * Cantine staff mobile app: today's menu + per-student allergy lookup + QR check-in.
 */
const CantineStaffApp = () => {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [menu, setMenu] = useState(null);
  const [students, setStudents] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [allergies, setAllergies] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [lastScan, setLastScan] = useState(null);
  const [count, setCount] = useState(0);

  useEffect(() => {
    cantineApi.todayMenu().then(({ data }) => setMenu(data)).catch(() => {});
    studentsApi.getAll().then(({ data }) => setStudents(data)).catch(() => {});
    qrApi.cantineAttendances().then(({ data }) => setCount(data.count || 0)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selected) { setAllergies(null); return; }
    cantineApi.getAllergies(selected.id).then(({ data }) => setAllergies(data)).catch(() => {});
  }, [selected]);

  const handleScan = async (token) => {
    setScanning(false);
    try {
      const { data } = await qrApi.cantineCheckin(token);
      setLastScan(data);
      setCount(c => c + 1);
      if ((data.allergies || []).length > 0) {
        toast.error(`⚠️ ${data.student_name}: ${data.allergies.join(', ')}`, { duration: 8000 });
      } else {
        toast.success(`✓ ${data.student_name}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || t('unknownQr'));
    }
  };

  const filtered = students.filter(s => {
    if (!search.trim()) return false;
    const q = search.toLowerCase();
    return (s.first_name || '').toLowerCase().includes(q) || (s.last_name || '').toLowerCase().includes(q);
  }).slice(0, 20);

  const doLogout = async () => { await logout(); navigate('/m/login', { replace: true }); };

  return (
    <div className="min-h-screen bg-[#F8FAFC] p-4 pb-24" data-testid="cantine-app">
      <header className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ForkKnife size={24} className="text-[#10B981]" weight="duotone" />
          <div>
            <p className="font-outfit font-bold text-[#0F172A]">{t('canteenStaff')}</p>
            <p className="text-xs text-[#64748B]">{user?.name}</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={() => navigate('/m/my-absences')} data-testid="cantine-my-absences" title="Mes absences">
            <CalendarX size={18} className="text-amber-600" />
          </Button>
          <Button variant="ghost" size="sm" onClick={doLogout} data-testid="cantine-logout"><SignOut size={18} /></Button>
        </div>
      </header>

      {/* QR scanner section */}
      {scanning ? (
        <div className="mb-4"><QrScanner onScan={handleScan} onClose={() => setScanning(false)} /></div>
      ) : (
        <Card className="border-2 border-[#10B981] shadow-none mb-4" data-testid="cantine-scan-card">
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center justify-between">
              <p className="font-semibold text-sm flex items-center gap-2">
                <QrCode size={18} className="text-[#10B981]" weight="duotone" />
                {t('canteenCheckinTitle')}
              </p>
              <span className="text-xs bg-[#10B981] text-white rounded-full px-2 py-0.5 font-bold" data-testid="cantine-count">{count}</span>
            </div>
            <Button className="w-full bg-[#10B981] hover:bg-[#059669]" onClick={() => setScanning(true)} data-testid="cantine-scan-btn">
              <QrCode size={18} className="mr-2" /> {t('scanQrButton')}
            </Button>
            {lastScan && (
              <div className={`text-xs p-2 rounded ${(lastScan.allergies || []).length ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`} data-testid="cantine-last-scan">
                <p className="font-bold">{lastScan.student_name}</p>
                {(lastScan.allergies || []).length > 0 && <p>⚠️ {t('cantineAllergyAlert')}: {lastScan.allergies.join(', ')}</p>}
                {lastScan.diet_notes && <p>{t('cantineDietLabel')}: {lastScan.diet_notes}</p>}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card className="border border-[#E2E8F0] shadow-none mb-4">
        <CardContent className="p-4">
          <h2 className="font-outfit font-semibold mb-2">{t('todayMenuShort')}</h2>
          {menu ? (
            <div className="text-sm space-y-1" data-testid="cantine-today-menu">
              <p>🥗 <span className="font-medium">{t('starter')}:</span> {menu.starter || '—'}</p>
              <p>🍽️ <span className="font-medium">{t('mainCourse')}:</span> {menu.main || '—'}</p>
              <p>🍰 <span className="font-medium">{t('dessert')}:</span> {menu.dessert || '—'}</p>
              {menu.notes && <p className="text-xs italic text-[#64748B] mt-2">{menu.notes}</p>}
            </div>
          ) : (
            <p className="text-sm text-[#94A3B8]">{t('noPublishedMenu')}</p>
          )}
        </CardContent>
      </Card>

      <Card className="border border-[#E2E8F0] shadow-none mb-4">
        <CardContent className="p-4 space-y-3">
          <h2 className="font-outfit font-semibold">{t('checkAllergies')}</h2>
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
            <Input
              placeholder={t('studentSearchPlaceholder')}
              value={search}
              onChange={e => { setSearch(e.target.value); setSelected(null); }}
              className="pl-9"
              data-testid="cantine-search"
            />
          </div>
          {filtered.length > 0 && !selected && (
            <ul className="space-y-1 max-h-48 overflow-auto" data-testid="cantine-search-results">
              {filtered.map(s => (
                <li key={s.id}>
                  <button
                    className="w-full text-left p-2 rounded hover:bg-[#F1F5F9] text-sm"
                    onClick={() => setSelected(s)}
                    data-testid={`cantine-pick-${s.id}`}
                  >
                    {s.first_name} {s.last_name} <span className="text-xs text-[#64748B]">{s.class_name || ''}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {selected && allergies && (
        <Card className="border-2 border-amber-300 shadow-none" data-testid="cantine-allergy-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Warning size={20} className="text-amber-600" weight="duotone" />
              <h3 className="font-outfit font-semibold">{allergies.student_name}</h3>
            </div>
            {(allergies.allergies || []).length > 0 ? (
              <div className="space-y-1">
                <p className="text-xs uppercase text-[#64748B]">{t('cantineAllergyAlert')}</p>
                <div className="flex flex-wrap gap-1">
                  {allergies.allergies.map((a, i) => (
                    <span key={i} className="bg-red-100 text-red-700 px-2 py-1 rounded-full text-xs font-medium">
                      {a}
                    </span>
                  ))}
                </div>
              </div>
            ) : <p className="text-sm text-green-600">✓ {t('noAllergies')}</p>}
            {allergies.diet_notes && (
              <div className="mt-3">
                <p className="text-xs uppercase text-[#64748B]">{t('cantineDietLabel')}</p>
                <p className="text-sm">{allergies.diet_notes}</p>
              </div>
            )}
            <Button variant="ghost" size="sm" onClick={() => { setSelected(null); setSearch(''); }} className="mt-3" data-testid="cantine-clear">
              {t('newSearch')}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CantineStaffApp;
